import React, { useEffect, useState, useRef, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const hover = new Date()
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

// Helper to convert numbers to Indian words system (Lakhs, Crores)
const numberToWords = (num) => {
  if (!num || isNaN(num) || num <= 0) return "ZERO ONLY.";
  const a = ["", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE", "TEN", "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN", "SEVENTEEN", "EIGHTEEN", "NINETEEN"];
  const b = ["", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY", "EIGHTY", "NINETY"];
  
  const makeGroup = (n) => {
    let words = "";
    if (n >= 100) {
      words += a[Math.floor(n / 100)] + " HUNDRED ";
      n %= 100;
    }
    if (n >= 20) {
      words += b[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      words += a[n] + " ";
    }
    return words.trim();
  };

  let str = "";
  let raw = Math.round(num);

  // Crore
  const crore = Math.floor(raw / 10000000);
  raw %= 10000000;
  if (crore > 0) {
    str += makeGroup(crore) + " CRORE ";
  }

  // Lakh
  const lakh = Math.floor(raw / 100000);
  raw %= 100000;
  if (lakh > 0) {
    str += makeGroup(lakh) + " LAKH ";
  }

  // Thousand
  const thousand = Math.floor(raw / 1000);
  raw %= 1000;
  if (thousand > 0) {
    str += makeGroup(thousand) + " THOUSAND ";
  }

  // Remainder
  if (raw > 0) {
    str += makeGroup(raw) + " ";
  }

  return str.trim() + " ONLY.";
};

const PfChallanReport = () => {
  const dispatch = useDispatch();

  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // Lists and loading states
  const [firmList, setFirmList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payrollParams, setPayrollParams] = useState(null);
  const [pfRate, setPfRate] = useState(null);
  const [allowanceMasterList, setAllowanceMasterList] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [accountGroupNo, setAccountGroupNo] = useState("");
  const [paymentDate, setPaymentDate] = useState("");

  // Sync session changes from storage
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fetch initial base listings
  useEffect(() => {
    // 1. Fetch firms list
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);

    // 2. Fetch allowance master list to identify PF applicability
    Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/AllowanceMaster/Id/0`)
      .then(data => setAllowanceMasterList(data || []))
      .catch(() => {});
  }, [dispatch]);

  // Load report data when firm, month, or year changes
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId) {
      loadReportData(firmId, selectedMonth, selectedYear);
    } else {
      setEmployees([]);
      setPayrollRecords([]);
      setPayrollParams(null);
      setPfRate(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear]);

  const loadReportData = async (firmId, monthName, year) => {
    setIsLoading(true);
    try {
      // 1. Fetch Employee List for PF details & names
      const empData = await Fn_FillListData(
        dispatch,
        () => {},
        "Data",
        `${API_WEB_URLS.MASTER}/0/token/EmployeeDataDynamic/0/${firmId}`
      );
      setEmployees(empData || []);

      // 2. Fetch PF Parameter ceilings
      const pfRes = await Fn_FillListData(
        dispatch,
        () => {},
        "Data",
        `${API_WEB_URLS.MASTER}/0/token/PfParamFetch/TBL.F_FirmMaster/${firmId}`
      );
      const sortedPf = (pfRes || []).sort((a, b) => new Date(a.PFDate) - new Date(b.PFDate));
      setPfRate(sortedPf.length > 0 ? sortedPf[sortedPf.length - 1] : null);

      // 3. Fetch Payroll Parameters
      const paramRes = await Fn_FillListData(
        dispatch,
        () => {},
        "Data",
        `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${firmId}`
      );
      setPayrollParams((paramRes && paramRes.length > 0) ? paramRes[0] : null);

      // 4. Fetch Saved Payroll Transactions for month/year
      if (monthName && year) {
        const monthNum = MONTH_NAMES.indexOf(monthName) + 1;
        const monthStr = String(monthNum).padStart(2, "0");

        const fd = new FormData();
        fd.append("Id", 0);
        fd.append("F_FirmMaster", firmId);
        fd.append("PayrollMonth", monthStr);
        fd.append("PayrollYear", year);

        const records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "GetPayrollTransaction/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );
        setPayrollRecords(records || []);
      } else {
        setPayrollRecords([]);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Find Firm Code No. (which is PF No. on the branch) and Address
  const getFirmPfNo = () => {
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  const getFirmAddress = () => {
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.OfficeAddress || mainBranch.CoAddress || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  // Format month and year helper
  const getFormattedMonthYear = () => {
    if (!selectedMonth) return "";
    const monthNum = MONTH_NAMES.indexOf(selectedMonth) + 1;
    const monthStr = String(monthNum).padStart(2, "0");
    return `${monthStr} / ${selectedYear}`;
  };

  // ─── Calculations ───
  let pfMembersCount = 0;
  let epsMembersCount = 0;
  let edliMembersCount = 0;
  let totalMembersCount = 0;

  let totalPfWages = 0;
  let totalEpsWages = 0;
  let totalEdliWages = 0;
  let totalGrossWages = 0;

  // Contributions columns
  let emplrAc1Share = 0;
  let emplrAc10Share = 0;
  let emplrAc21Share = 0;
  let employeeAc1Share = 0;
  let ac2AdminCharges = 0;
  let ac22AdminCharges = 0;

  // Loop through saved payroll records to aggregate values
  payrollRecords.forEach(record => {
    // Find matching employee details
    const emp = employees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
    if (!emp) return;

    totalMembersCount++;
    totalGrossWages += parseFloat(record.GrossAmount || 0);

    let empAllowances = [];
    try {
      empAllowances = emp.AllowanceJson ? JSON.parse(emp.AllowanceJson) : [];
    } catch {}

    // Verify if PF is applicable
    if (record.PFApplicable && parseFloat(record.PF || 0) > 0) {
      pfMembersCount++;
      edliMembersCount++;

      // Compute PF wages base (uncapped)
      let pfBase = parseFloat(record.Wages || 0);
      for (let i = 1; i <= 10; i++) {
        const alName = record[`Allowance${i}Name`] || "";
        const alVal = parseFloat(record[`CalculatedAllowance${i}`] || 0);
        if (alName && alVal > 0) {
          const master = allowanceMasterList.find(m => m.Name?.toLowerCase() === alName.toLowerCase());
          if (master) {
            const empAl = empAllowances.find(ea => String(ea.F_AllowanceMaster) === String(master.Id));
            if (empAl && empAl.IsPFApply && empAl.IsApplicable) {
              pfBase += alVal;
            }
          }
        }
      }

      if (payrollParams?.IsPFOnOT) {
        pfBase += parseFloat(record.OverTime || 0);
      }
      if (payrollParams?.IsPFOnArrear) {
        pfBase += parseFloat(record.Arrear || 0);
      }
      if (payrollParams?.IsPFOnExtraPay) {
        pfBase += parseFloat(record.ExtraPay || 0);
      }
      if (payrollParams?.IsPFOnExtraPay1) {
        pfBase += parseFloat(record.ExtraPay1 || 0);
      }

      // Cap at PF ceiling
      const pfCeiling = parseFloat(pfRate?.MaxWagesForPF || 15000);
      const cappedPfWages = Math.min(pfBase, pfCeiling);
      totalPfWages += cappedPfWages;
      totalEdliWages += cappedPfWages; // EDLI shares the same ceiling

      // Aggregate contribution shares
      employeeAc1Share += parseFloat(record.PF || 0);
      emplrAc1Share += parseFloat(record.EmplrPF || 0);
      emplrAc21Share += parseFloat(record.AC21 || 0);
      ac2AdminCharges += parseFloat(record.AdminCharges || 0);
      ac22AdminCharges += parseFloat(record.AC22 || 0);

      // Verify EPS Pension membership
      if (parseFloat(record.Pension || 0) > 0) {
        epsMembersCount++;
        
        // Pension wages is capped basic salary
        const pensionCeiling = parseFloat(pfRate?.MaxWagesForPension || 15000);
        const cappedPensionWages = Math.min(parseFloat(record.Wages || 0), pensionCeiling);
        totalEpsWages += cappedPensionWages;

        emplrAc10Share += parseFloat(record.Pension || 0);
      }
    }
  });

  // Rounded values
  const rEmployeeAc1 = Math.round(employeeAc1Share);
  const rEmplrAc1 = Math.round(emplrAc1Share);
  const rEmplrAc10 = Math.round(emplrAc10Share);
  const rEmplrAc21 = Math.round(emplrAc21Share);
  const rAc2Admin = Math.round(ac2AdminCharges);
  const rAc22Admin = Math.round(ac22AdminCharges);

  // Particulars rows totals
  const totalEmployerShare = rEmplrAc1 + rEmplrAc10 + rEmplrAc21;
  const totalEmployeeShare = rEmployeeAc1;
  const totalAdminCharges = rAc2Admin + rAc22Admin;

  // Account column totals
  const col1Total = rEmplrAc1 + rEmployeeAc1;
  const col2Total = rAc2Admin;
  const col10Total = rEmplrAc10;
  const col21Total = rEmplrAc21;
  const col22Total = rAc22Admin;

  // Grand Total Amount (overall sum)
  const grandTotalAmount = col1Total + col2Total + col10Total + col21Total + col22Total;

  // Excel Export
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("PF Challan");

      // Title header rows
      worksheet.addRow(["COMBINED CHALLAN A/C NO. 1, 2, 10, 21 AND 22"]);
      worksheet.addRow(["(STATE BANK OF INDIA)"]);
      worksheet.addRow(["EMPLOYEE'S PROVIDENT FUND ORGANIZATION"]);
      worksheet.addRow([]); // empty

      // Basic fields
      worksheet.addRow(["Code No.:", getFirmPfNo(), "A/C Group No.:", accountGroupNo, "Paid by : Cheque/Cash"]);
      worksheet.addRow([]);

      // Summary table headers
      const sumH = worksheet.addRow(["", "AC 1", "AC 10", "AC 21", "Tot Wages", "Month: " + (selectedMonth ? selectedMonth.toUpperCase() : "") + " " + selectedYear]);
      sumH.font = { bold: true };
      
      worksheet.addRow(["No. of Members :", pfMembersCount, epsMembersCount, edliMembersCount, totalMembersCount]);
      worksheet.addRow(["Total Wages Payable:", Math.round(totalPfWages), Math.round(totalEpsWages), Math.round(totalEdliWages), Math.round(totalGrossWages)]);
      worksheet.addRow([]);

      // Main Table
      const header = worksheet.addRow(["PARTICULAR", "A/C NO. 1", "A/C NO. 2", "A/C NO. 10", "A/C NO. 21", "A/C NO. 22", "TOTAL"]);
      header.font = { bold: true, color: { argb: "FFFFFFFF" } };
      header.eachCell(cell => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF2C3E50" }
        };
        cell.alignment = { horizontal: "center" };
      });

      worksheet.addRow(["1. Employers Share of Contribution", rEmplrAc1, "", rEmplrAc10, rEmplrAc21, "", totalEmployerShare]);
      worksheet.addRow(["2. Employees Share of Contribution", rEmployeeAc1, "", "", "", "", totalEmployeeShare]);
      worksheet.addRow(["3. Adm. Charges", "", rAc2Admin, "", "", rAc22Admin, totalAdminCharges]);
      worksheet.addRow(["4. Inspection Charges", "", "", "", "", "", 0]);
      worksheet.addRow(["5. Penal Damages", "", "", "", "", "", ""]);
      worksheet.addRow(["6. Misc. Payment", "", "", "", "", "", ""]);

      const totalRow = worksheet.addRow(["TOTAL", col1Total, col2Total, col10Total, col21Total, col22Total, grandTotalAmount]);
      totalRow.font = { bold: true };
      totalRow.eachCell(cell => {
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "double", color: { argb: "FF000000" } }
        };
      });

      worksheet.addRow([]);
      worksheet.addRow(["Amount (in words) :", numberToWords(grandTotalAmount)]);
      worksheet.addRow(["Institution Name :", sessionFirm?.Name || ""]);
      worksheet.addRow(["Address :", getFirmAddress()]);

      // Column sizing
      worksheet.columns.forEach((column, i) => {
        column.width = i === 0 ? 35 : 18;
      });

      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `PF_Combined_Challan_${selectedMonth}_${selectedYear}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Excel export failed.");
    }
  };

  // PDF Export
  const exportToPDF = () => {
    const pdf = new jsPDF("p", "mm", "a4");

    pdf.setFontSize(11);
    pdf.setFont("Helvetica", "bold");
    pdf.text("COMBINED CHALLAN A/C NO. 1, 2, 10, 21 AND 22", 105, 12, { align: "center" });
    pdf.text("(STATE BANK OF INDIA)", 105, 17, { align: "center" });
    pdf.setFontSize(12);
    pdf.text("EMPLOYEE'S PROVIDENT FUND ORGANIZATION", 105, 23, { align: "center" });

    pdf.setFontSize(9);
    pdf.setFont("Helvetica", "normal");
    pdf.text(`Code No.: ${getFirmPfNo()}`, 14, 32);
    pdf.text(`A/C Group No.: ${accountGroupNo}`, 85, 32);
    pdf.text("Paid by : Cheque/Cash", 150, 32);

    // Summary ECR grid
    autoTable(pdf, {
      head: [["No. of Members :", `AC 1: ${pfMembersCount}`, `AC 10: ${epsMembersCount}`, `AC 21: ${edliMembersCount}`, `Month: ${(selectedMonth || "").toUpperCase()} ${selectedYear}`]],
      body: [
        ["Total Wages Payable:", Math.round(totalPfWages), Math.round(totalEpsWages), Math.round(totalEdliWages), `Tot Wages: ${Math.round(totalGrossWages)}`]
      ],
      startY: 36,
      theme: "plain",
      styles: { fontSize: 8, fontStyle: "bold" },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] }
    });

    const particularRows = [
      ["1. Employers Share of Contribution", rEmplrAc1 || "", "", rEmplrAc10 || "", rEmplrAc21 || "", "", totalEmployerShare],
      ["2. Employees Share of Contribution", rEmployeeAc1 || "", "", "", "", "", totalEmployeeShare],
      ["3. Adm. Charges", "", rAc2Admin || "", "", "", rAc22Admin || "", totalAdminCharges],
      ["4. Inspection Charges", "", "", "", "", "", "0"],
      ["5. Penal Damages", "", "", "", "", "", ""],
      ["6. Misc. Payment", "", "", "", "", "", ""],
      [
        { content: "TOTAL", styles: { fontStyle: "bold" } },
        { content: col1Total, styles: { fontStyle: "bold" } },
        { content: col2Total, styles: { fontStyle: "bold" } },
        { content: col10Total, styles: { fontStyle: "bold" } },
        { content: col21Total, styles: { fontStyle: "bold" } },
        { content: col22Total, styles: { fontStyle: "bold" } },
        { content: grandTotalAmount, styles: { fontStyle: "bold" } }
      ]
    ];

    autoTable(pdf, {
      head: [["PARTICULAR", "A/C NO. 1", "A/C NO. 2", "A/C NO. 10", "A/C NO. 21", "A/C NO. 22", "TOTAL"]],
      body: particularRows,
      startY: pdf.lastAutoTable.finalY + 6,
      styles: {
        fontSize: 8.5,
        cellPadding: 3,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center"
      },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { halign: "right", cellWidth: 22 },
        2: { halign: "right", cellWidth: 22 },
        3: { halign: "right", cellWidth: 22 },
        4: { halign: "right", cellWidth: 22 },
        5: { halign: "right", cellWidth: 22 },
        6: { halign: "right", cellWidth: 22 }
      },
      theme: "grid"
    });

    const finalY = pdf.lastAutoTable.finalY + 8;
    pdf.setFont("Helvetica", "bold");
    pdf.text(`Amount (in words) : ${numberToWords(grandTotalAmount).toUpperCase()}`, 14, finalY);
    pdf.text(`Institution Name : ${sessionFirm?.Name || ""}`, 14, finalY + 6);
    pdf.text(`Address : ${getFirmAddress()}`, 14, finalY + 12);

    pdf.save(`PF_Combined_Challan_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-challan-container, #print-challan-container * {
        visibility: visible !important;
      }
      #print-challan-container {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        padding: 15px;
        background: #fff !important;
        color: #000 !important;
        font-family: 'Courier New', Courier, monospace !important;
      }
      .no-print {
        display: none !important;
      }
      .challan-print-table {
        border-collapse: collapse !important;
        width: 100% !important;
        margin-top: 10px !important;
      }
      .challan-print-table th, .challan-print-table td {
        border: 1px solid #000 !important;
        color: #000 !important;
        padding: 5px 8px !important;
        font-size: 11px !important;
        background-color: transparent !important;
      }
      .challan-print-table th {
        font-weight: bold !important;
        text-align: center !important;
      }
      .challan-print-table td.text-end {
        text-align: right !important;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="PF Challan Report" motherMenu="Reports" pageContent="Combined Challan" />
      </div>

      {/* Modern Filter Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm Dropdown */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Month Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Account Group Input */}
            <Col lg={4} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>A/C Group No.</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Enter A/C Group No. (Optional)"
                value={accountGroupNo}
                onChange={(e) => setAccountGroupNo(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** from the filters above or from the **Dashboard Home** to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* ─── WEB PREVIEW VIEW (Modern Indigo/Slate Theme) ─── */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff", overflow: "hidden" }}>
              <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom py-3 px-4">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Combined Challan (PF Accounts 1, 2, 10, 21, 22)
                  </h5>
                  <small className="text-muted fs-12">
                    PF Code: <strong className="text-primary">{getFirmPfNo()}</strong> &nbsp;|&nbsp; Period: <strong>{(selectedMonth || "").toUpperCase()} {selectedYear}</strong>
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="danger" size="sm" className="px-3" style={{ borderRadius: "5px", fontSize: "12px", background: "#E74C3C", border: "none" }} onClick={exportToPDF}>
                    <i className="fa fa-file-pdf-o me-2" />
                    PDF
                  </Button>
                  <Button variant="success" size="sm" className="px-3" style={{ borderRadius: "5px", fontSize: "12px", background: "#27AE60", border: "none" }} onClick={exportToExcel}>
                    <i className="fa fa-file-excel-o me-2" />
                    Excel
                  </Button>
                  <Button variant="primary" size="sm" className="px-3 text-white" style={{ borderRadius: "5px", fontSize: "12px", background: "#2980B9", border: "none" }} onClick={() => window.print()}>
                    <i className="fa fa-print me-2" />
                    Print Challan
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Calculating Challan Dues...</div>
                  </div>
                ) : (
                  <Fragment>
                    {/* ECR Summary Metrics Grid (Aligned with reference screenshot layout) */}
                    <div className="mb-4 p-4 border rounded bg-white shadow-xs">
                      <Row className="g-3">
                        <Col lg={8} md={12}>
                          <Table bordered responsive size="sm" className="mb-3 text-center align-middle" style={{ fontSize: "12px" }}>
                            <thead>
                              <tr className="bg-light text-secondary">
                                <th></th>
                                <th style={{ width: "18%" }}>AC 1</th>
                                <th style={{ width: "18%" }}>AC 10</th>
                                <th style={{ width: "18%" }}>AC 21</th>
                                <th style={{ width: "22%" }}>Tot Wages</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="text-start fw-semibold text-dark">No. of Members :</td>
                                <td className="fw-bold text-dark">{pfMembersCount}</td>
                                <td className="fw-bold text-dark">{epsMembersCount}</td>
                                <td className="fw-bold text-dark">{edliMembersCount}</td>
                                <td className="fw-bold text-dark">{totalMembersCount}</td>
                              </tr>
                              <tr>
                                <td className="text-start fw-semibold text-dark">Total Wages Payable:</td>
                                <td className="font-monospace text-secondary">₹{Math.round(totalPfWages).toLocaleString('en-IN')}</td>
                                <td className="font-monospace text-secondary">₹{Math.round(totalEpsWages).toLocaleString('en-IN')}</td>
                                <td className="font-monospace text-secondary">₹{Math.round(totalEdliWages).toLocaleString('en-IN')}</td>
                                <td className="font-monospace fw-bold text-primary">₹{Math.round(totalGrossWages).toLocaleString('en-IN')}</td>
                              </tr>
                            </tbody>
                          </Table>

                          <div className="d-flex flex-column gap-2" style={{ maxWidth: "450px" }}>
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: "140px", fontSize: "12px", color: "#6C757D" }}>Part of Employer :</span>
                              <div className="py-1 px-3 border rounded font-monospace bg-light" style={{ width: "160px", textAlign: "center", fontSize: "12px", fontWeight: "bold" }}>
                                {getFormattedMonthYear()}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: "140px", fontSize: "12px", color: "#6C757D" }}>Part of Employee :</span>
                              <div className="py-1 px-3 border rounded font-monospace bg-light" style={{ width: "160px", textAlign: "center", fontSize: "12px", fontWeight: "bold" }}>
                                {getFormattedMonthYear()}
                              </div>
                            </div>
                          </div>
                        </Col>

                        <Col lg={4} md={12} className="d-flex flex-column justify-content-between align-items-end pe-lg-3">
                          <div className="d-flex align-items-center gap-2 mb-3 mb-lg-0">
                            <span style={{ fontSize: "12px", color: "#6C757D" }}>Month:</span>
                            <div className="py-1 px-3 border rounded font-monospace bg-light fw-bold text-dark" style={{ minWidth: "150px", textAlign: "center", fontSize: "12px" }}>
                              {(selectedMonth || "").toUpperCase()} {selectedYear}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <span style={{ fontSize: "12px", color: "#6C757D" }}>Date of Payment :</span>
                            <input 
                              type="date" 
                              className="form-control form-control-sm font-monospace"
                              style={{ width: "150px", height: "32px", fontSize: "12px" }}
                              value={paymentDate}
                              onChange={(e) => setPaymentDate(e.target.value)}
                            />
                          </div>
                        </Col>
                      </Row>
                    </div>

                    {/* Main Challan Accounts Grid */}
                    <div className="table-responsive">
                      <Table hover bordered striped className="align-middle mb-4" style={{ fontSize: "13px" }}>
                        <thead className="bg-light">
                          <tr className="border-bottom-2 text-center fw-bold">
                            <th className="text-start" style={{ width: "35%" }}>PARTICULAR</th>
                            <th style={{ width: "11%" }}>A/C NO. 1</th>
                            <th style={{ width: "11%" }}>A/C NO. 2</th>
                            <th style={{ width: "11%" }}>A/C NO. 10</th>
                            <th style={{ width: "11%" }}>A/C NO. 21</th>
                            <th style={{ width: "11%" }}>A/C NO. 22</th>
                            <th style={{ width: "10%" }}>TOTAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="fw-semibold">1. Employer's Share of Contribution</td>
                            <td className="text-end font-monospace">₹{rEmplrAc1.toLocaleString('en-IN')}</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-end font-monospace">₹{rEmplrAc10.toLocaleString('en-IN')}</td>
                            <td className="text-end font-monospace">₹{rEmplrAc21.toLocaleString('en-IN')}</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-end font-monospace fw-bold">₹{totalEmployerShare.toLocaleString('en-IN')}</td>
                          </tr>
                          <tr>
                            <td className="fw-semibold">2. Employee's Share of Contribution</td>
                            <td className="text-end font-monospace">₹{rEmployeeAc1.toLocaleString('en-IN')}</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-end font-monospace fw-bold">₹{totalEmployeeShare.toLocaleString('en-IN')}</td>
                          </tr>
                          <tr>
                            <td className="fw-semibold">3. Admin Charges</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-end font-monospace">₹{rAc2Admin.toLocaleString('en-IN')}</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-end font-monospace">₹{rAc22Admin.toLocaleString('en-IN')}</td>
                            <td className="text-end font-monospace fw-bold">₹{totalAdminCharges.toLocaleString('en-IN')}</td>
                          </tr>
                          <tr>
                            <td className="fw-semibold text-muted">4. Inspection Charges</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-end text-muted font-monospace">₹0.00</td>
                          </tr>
                          <tr className="bg-light fw-bold" style={{ borderTop: "2px solid #DEE2E6" }}>
                            <td>GRAND TOTALS</td>
                            <td className="text-end font-monospace">₹{col1Total.toLocaleString('en-IN')}</td>
                            <td className="text-end font-monospace">₹{col2Total.toLocaleString('en-IN')}</td>
                            <td className="text-end font-monospace">₹{col10Total.toLocaleString('en-IN')}</td>
                            <td className="text-end font-monospace">₹{col21Total.toLocaleString('en-IN')}</td>
                            <td className="text-end font-monospace">₹{col22Total.toLocaleString('en-IN')}</td>
                            <td className="text-end text-primary font-monospace fs-14">₹{grandTotalAmount.toLocaleString('en-IN')}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>

                    {/* Word Amount & Institution Card */}
                    <div className="p-3 bg-light rounded border" style={{ fontSize: "12px" }}>
                      <div className="mb-2">
                        <strong>Amount (in words):</strong> &nbsp;
                        <span className="text-uppercase text-secondary fw-semibold font-monospace">{numberToWords(grandTotalAmount)}</span>
                      </div>
                      <div className="mb-2">
                        <strong>Institution Name:</strong> &nbsp;
                        <span className="text-dark fw-bold">{sessionFirm?.Name?.toUpperCase()}</span>
                      </div>
                      <div>
                        <strong>Address:</strong> &nbsp;
                        <span className="text-secondary">{getFirmAddress()}</span>
                      </div>
                    </div>
                  </Fragment>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* ─── PHYSICAL PRINTING VIEW (EPFO Combined Challan Copy) ─── */}
          <div id="print-challan-container" className="d-none">
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "14px" }}>
              COMBINED CHALLAN A/C NO. 1, 2, 10, 21 AND 22
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "11px", marginTop: "2px" }}>
              (STATE BANK OF INDIA)
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "13px", margin: "4px 0" }}>
              EMPLOYEE'S PROVIDENT FUND ORGANIZATION
            </div>
            <div style={{ textAlign: "right", fontStyle: "italic", fontSize: "11px", fontWeight: "bold" }}>
              Original Copy
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", marginBottom: "8px", fontSize: "11px" }}>
              <div><strong>Code No.:</strong> {getFirmPfNo()}</div>
              <div><strong>A/C Group No.:</strong> {accountGroupNo || "___________________"}</div>
              <div><strong>Paid by :</strong> Cheque/Cash</div>
            </div>

            {/* ECR Summary Grid Table */}
            <table className="challan-print-table">
              <thead>
                <tr style={{ background: "#eee" }}>
                  <th style={{ border: "1px solid #000" }}></th>
                  <th style={{ border: "1px solid #000" }}>AC 1</th>
                  <th style={{ border: "1px solid #000" }}>AC 10</th>
                  <th style={{ border: "1px solid #000" }}>AC 21</th>
                  <th style={{ border: "1px solid #000" }}>Tot Wages</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #000" }}><strong>No. of Members :</strong></td>
                  <td style={{ border: "1px solid #000", textAlign: "center", fontWeight: "bold" }}>{pfMembersCount}</td>
                  <td style={{ border: "1px solid #000", textAlign: "center", fontWeight: "bold" }}>{epsMembersCount}</td>
                  <td style={{ border: "1px solid #000", textAlign: "center", fontWeight: "bold" }}>{edliMembersCount}</td>
                  <td style={{ border: "1px solid #000", textAlign: "center", fontWeight: "bold" }}>{totalMembersCount}</td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #000" }}><strong>Total Wages Payable:</strong></td>
                  <td style={{ border: "1px solid #000", textAlign: "right", fontWeight: "bold" }}>{Math.round(totalPfWages)}</td>
                  <td style={{ border: "1px solid #000", textAlign: "right", fontWeight: "bold" }}>{Math.round(totalEpsWages)}</td>
                  <td style={{ border: "1px solid #000", textAlign: "right", fontWeight: "bold" }}>{Math.round(totalEdliWages)}</td>
                  <td style={{ border: "1px solid #000", textAlign: "right", fontWeight: "bold" }}>{Math.round(totalGrossWages)}</td>
                </tr>
              </tbody>
            </table>

            {/* Print Period & Payment Dates */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontSize: "11px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div><strong>Part of Employer :</strong> {getFormattedMonthYear()}</div>
                <div><strong>Part of Employee :</strong> {getFormattedMonthYear()}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end" }}>
                <div><strong>Month:</strong> &nbsp;&nbsp;{(selectedMonth || "").toUpperCase()} {selectedYear}</div>
                <div><strong>Date of Payment :</strong> {paymentDate ? new Date(paymentDate).toLocaleDateString('en-GB') : "___________________"}</div>
              </div>
            </div>

            {/* Main Accounts Table */}
            <table className="challan-print-table" style={{ marginTop: "12px" }}>
              <thead>
                <tr>
                  <th style={{ width: "35%", textAlign: "left" }}>PARTICULAR</th>
                  <th style={{ width: "11%" }}>A/C NO. 1</th>
                  <th style={{ width: "11%" }}>A/C NO. 2</th>
                  <th style={{ width: "11%" }}>A/C NO. 10</th>
                  <th style={{ width: "11%" }}>A/C NO. 21</th>
                  <th style={{ width: "11%" }}>A/C NO. 22</th>
                  <th style={{ width: "10%" }}>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1. Employers Share of Contribution</td>
                  <td className="text-end">{rEmplrAc1 || ""}</td>
                  <td></td>
                  <td className="text-end">{rEmplrAc10 || ""}</td>
                  <td className="text-end">{rEmplrAc21 || ""}</td>
                  <td></td>
                  <td className="text-end" style={{ fontWeight: "bold" }}>{totalEmployerShare}</td>
                </tr>
                <tr>
                  <td>2. Employees Share of Contribution</td>
                  <td className="text-end">{rEmployeeAc1 || ""}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="text-end" style={{ fontWeight: "bold" }}>{totalEmployeeShare}</td>
                </tr>
                <tr>
                  <td>3. Adm. Charges</td>
                  <td></td>
                  <td className="text-end">{rAc2Admin || ""}</td>
                  <td></td>
                  <td></td>
                  <td className="text-end">{rAc22Admin || ""}</td>
                  <td className="text-end" style={{ fontWeight: "bold" }}>{totalAdminCharges}</td>
                </tr>
                <tr>
                  <td>4. Inspection Charges</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="text-end">0</td>
                </tr>
                <tr>
                  <td>5. Penal Demages</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td>6. Misc. Payment</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr style={{ fontWeight: "bold" }}>
                  <td>TOTAL</td>
                  <td className="text-end">{col1Total}</td>
                  <td className="text-end">{col2Total}</td>
                  <td className="text-end">{col10Total}</td>
                  <td className="text-end">{col21Total}</td>
                  <td className="text-end">{col22Total}</td>
                  <td className="text-end">{grandTotalAmount}</td>
                </tr>
              </tbody>
            </table>

            <div style={{ marginTop: "15px", fontSize: "11px", lineHeight: "1.6" }}>
              <div><strong>Amount (in words) :</strong> <span style={{ textDecoration: "underline" }}>{numberToWords(grandTotalAmount).toUpperCase()}</span></div>
              <div style={{ marginTop: "5px" }}><strong>Institution Name :</strong> <strong>{sessionFirm?.Name?.toUpperCase()}</strong></div>
              <div><strong>Address :</strong> {getFirmAddress()}</div>
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default PfChallanReport;
